import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Business as IceIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { BonLivraison, BonLivraisonCreate, BonLivraisonUpdate, BonLivraisonItemCreate, BonLivraisonItemUpdate, BonLivraisonStatus } from '../../types/bonLivraison';
import { Client } from '../../types/client';
import { Article } from '../../types';
import {
  bonsLivraisonAPI,
  clientsAPI,
  articlesAPI,
} from '../../services/api';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import useFormDraft from '../../hooks/useFormDraft';
import { extractErrorMessage } from '../../utils/errorHandler';
import { useClientPlafond } from '../../hooks/useClientPlafond';
import {
  SlideUpTransition,
  useMinimizable,
  MinimizeButton,
  MinimizedBar,
} from '../../components/MinimizableDialog';

interface LineItem {
  id?: number | null;  // For existing items during edit
  article_id: number | null | undefined;
  designation: string;
  quantite: number | null | string;
  commentaire: string;
  article?: Article;
}

interface BonLivraisonFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  bonLivraisonId?: number;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const BonLivraisonFormDialog: React.FC<BonLivraisonFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  bonLivraisonId,
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const isEdit = !!bonLivraisonId;

  const [loading, setLoading] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );
  const [clients, setClients] = useState<Client[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Use reusable hook for plafond check
  const { clientSummary, isPlafondDepasse } = useClientPlafond(selectedClient?.id);

  const [formData, setFormData] = useState<{
    date: string;
    bon_commande_number: string;
    transporteur_nom: string;
    transporteur_matricule: string;
    transporteur_cin: string;
    condition_remarque: string;
    status: string;
  }>({
    date: new Date().toISOString().split('T')[0],
    bon_commande_number: '',
    transporteur_nom: '',
    transporteur_matricule: '',
    transporteur_cin: '',
    condition_remarque: '',
    status: BonLivraisonStatus.EN_COURS as string,
  });

  const [lineItems, setLineItems] = useState<LineItem[]>([
    { article_id: null, designation: '', quantite: null, commentaire: '' },
  ]);

  const { alertState, showError, showSuccess, hideAlert } = useAlert();

  // Draft caching — only for new bons de livraison (never when editing an existing row).
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    formData: typeof formData;
    selectedClient: Client | null;
    lineItems: LineItem[];
  }>('bon-livraison', !isEdit);

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      bon_commande_number: '',
      transporteur_nom: '',
      transporteur_matricule: '',
      transporteur_cin: '',
      condition_remarque: '',
      status: BonLivraisonStatus.EN_COURS as string,
    });
    setSelectedClient(null);
    setLineItems([{ article_id: null, designation: '', quantite: null, commentaire: '' }]);
  };

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        try {
          const [clientsData, articlesData] = await Promise.all([
            clientsAPI.getAllForDropdown(),
            articlesAPI.getAllForDropdown(),
          ]);
          setClients(clientsData);
          setArticles(articlesData);

          if (isEdit && bonLivraisonId) {
            const bl = await bonsLivraisonAPI.getById(bonLivraisonId);
            populateForm(bl, clientsData);
          } else {
            const draft = loadDraft();
            if (draft) {
              setFormData(draft.formData);
              setSelectedClient(draft.selectedClient);
              setLineItems(draft.lineItems);
            } else {
              resetForm();
            }
          }
        } catch (err) {
          showError('Erreur lors du chargement des données');
          console.error(err);
        }
      };
      fetchData();
    }
  }, [open, bonLivraisonId, isEdit]);

  const populateForm = (
    bl: BonLivraison,
    clientsList: Client[]
  ) => {
    setFormData({
      date: bl.date,
      bon_commande_number: bl.bon_commande_number || '',
      transporteur_nom: bl.transporteur_nom || '',
      transporteur_matricule: bl.transporteur_matricule || '',
      transporteur_cin: bl.transporteur_cin || '',
      condition_remarque: bl.condition_remarque || '',
      status: bl.status,
    });

    const client = clientsList.find((c) => c.id === bl.client_id);
    setSelectedClient(client || null);

    if (bl.items && bl.items.length > 0) {
      setLineItems(
        bl.items.map((item) => ({
          id: item.id,  // Preserve item ID for update
          article_id: item.article_id,
          designation: item.designation,
          quantite: typeof item.quantite === 'string' ? parseFloat(item.quantite) : item.quantite,
          commentaire: item.commentaire || '',
        }))
      );
    }
  };

  const handleArticleChange = (index: number, article: Article | null) => {
    const newItems = [...lineItems];
    if (article) {
      newItems[index] = {
        ...newItems[index],
        article_id: article.id,
        designation: article.designation,
        article,
      };
    } else {
      newItems[index] = {
        ...newItems[index],
        article_id: null,
        designation: '',
        article: undefined,
      };
    }
    setLineItems(newItems);
  };

  const handleItemChange = (index: number, field: keyof LineItem, value: any) => {
    const newItems = [...lineItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setLineItems(newItems);
  };

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { article_id: null, designation: '', quantite: null, commentaire: '' },
    ]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async () => {
    if (!selectedClient) {
      showError('Veuillez sélectionner un client');
      return;
    }

    const validItems = lineItems.filter((item) => {
      const quantite = typeof item.quantite === 'string'
        ? parseFloat(item.quantite.replace(',', '.'))
        : item.quantite;
      return item.designation && item.designation.trim() !== '' && quantite && quantite > 0;
    });

    if (validItems.length === 0) {
      showError('Veuillez ajouter au moins un article');
      return;
    }

    try {
      setLoading(true);

      if (isEdit && bonLivraisonId) {
        // For update: include item IDs to update in place
        const updateItems: BonLivraisonItemUpdate[] = validItems.map((item) => {
          const quantite = typeof item.quantite === 'string'
            ? parseFloat(item.quantite.replace(',', '.')) || 0
            : (item.quantite ?? 0);

          return {
            id: item.id ?? null,  // Include ID for existing items
            article_id: item.article_id ?? null,
            designation: item.designation,
            quantite: Math.round(quantite * 100) / 100,
            commentaire: item.commentaire || null,
          };
        });

        const updateData: BonLivraisonUpdate = {
          date: formData.date,
          client_id: selectedClient.id,
          bon_commande_number: formData.bon_commande_number || null,
          transporteur_nom: formData.transporteur_nom || null,
          transporteur_matricule: formData.transporteur_matricule || null,
          transporteur_cin: formData.transporteur_cin || null,
          condition_remarque: formData.condition_remarque || null,
          status: formData.status,
          items: updateItems,
        };
        await bonsLivraisonAPI.update(bonLivraisonId, updateData);
        showSuccess('Bon de livraison modifié avec succès');
      } else {
        // For create: no IDs needed
        const createItems: BonLivraisonItemCreate[] = validItems.map((item) => {
          const quantite = typeof item.quantite === 'string'
            ? parseFloat(item.quantite.replace(',', '.')) || 0
            : (item.quantite ?? 0);

          return {
            article_id: item.article_id ?? null,
            designation: item.designation,
            quantite: Math.round(quantite * 100) / 100,
            commentaire: item.commentaire || null,
          };
        });

        const createData: BonLivraisonCreate = {
          date: formData.date,
          client_id: selectedClient.id,
          bon_commande_number: formData.bon_commande_number || null,
          transporteur_nom: formData.transporteur_nom || null,
          transporteur_matricule: formData.transporteur_matricule || null,
          transporteur_cin: formData.transporteur_cin || null,
          condition_remarque: formData.condition_remarque || null,
          items: createItems,
        };
        await bonsLivraisonAPI.create(createData);
        showSuccess('Bon de livraison créé avec succès');
      }

      clearDraft();
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1000);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // X / ANNULER = deliberate cancel: wipe inputs and discard any draft.
  const handleClose = () => {
    clearDraft();
    resetForm();
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      saveDraft({ formData, selectedClient, lineItems });
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
        TransitionComponent={SlideUpTransition}
        PaperProps={{
          sx: { minHeight: '80vh' },
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 600 }}>
              {isEdit ? 'Modifier le Bon de Livraison' : 'Nouveau Bon de Livraison'}
            </Typography>
            <Box display="flex" alignItems="center">
              <MinimizeButton color="#4CAF50" onClick={minimize} />
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* General Information */}
          <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
            Informations Générales
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                size="small"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={clients}
                getOptionLabel={(option) => `${option.code} - ${option.name}`}
                value={selectedClient}
                onChange={(_, value) => setSelectedClient(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} label="Client" required size="small" />
                )}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="N° Bon de Commande"
                fullWidth
                size="small"
                value={formData.bon_commande_number}
                onChange={(e) => setFormData({ ...formData, bon_commande_number: e.target.value })}
              />
            </Grid>
          </Grid>

          {/* Client Contact Info */}
          {selectedClient && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'rgba(76, 175, 80, 0.08)',
                borderRadius: 1,
                border: '1px solid rgba(76, 175, 80, 0.3)',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1, fontWeight: 600 }}>
                Informations du Client
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <IceIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                    <Typography variant="body2">
                      <strong>ICE:</strong> {selectedClient.ice ? selectedClient.ice : 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                    <Typography variant="body2">
                      {selectedClient.name ? selectedClient.name : 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                    <Typography variant="body2">
                      {selectedClient.address ? selectedClient.address : 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Plafond Warning */}
          {selectedClient && isPlafondDepasse && !isEdit && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                borderRadius: 1,
                border: '1px solid #F44336',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <WarningIcon sx={{ color: '#F44336', fontSize: 32 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ color: '#F44336', fontWeight: 600 }}>
                  {clientSummary?.plafond_fixe === 0 ? 'Aucun crédit autorisé !' : 'Plafond de crédit dépassé !'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {clientSummary?.plafond_fixe === 0 ? (
                    <>Ce client n'a pas de plafond de crédit autorisé. Impossible de créer un nouveau bon de livraison.</>
                  ) : (
                    <>
                      Ce client a dépassé son plafond de crédit de{' '}
                      <strong style={{ color: '#F44336' }}>
                        {clientSummary?.plafond_depasse.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                      </strong>
                      . Impossible de créer un nouveau bon de livraison.
                    </>
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Plafond fixé: {clientSummary?.plafond_fixe.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH |
                  Total impayé: {clientSummary?.total_impayee.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                </Typography>
              </Box>
            </Box>
          )}

          <Grid container spacing={2} sx={{ mt: 0 }}>
            {isEdit && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.status}
                    label="Statut"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value={BonLivraisonStatus.EN_COURS}>En cours</MenuItem>
                    <MenuItem value={BonLivraisonStatus.LIVRE}>Livré</MenuItem>
                    <MenuItem value={BonLivraisonStatus.ANNULE}>Annulé</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Articles Section */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" sx={{ color: '#4CAF50', fontWeight: 600 }}>
              Articles
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addLineItem}
              sx={{ borderColor: '#4CAF50', color: '#4CAF50' }}
            >
              Ajouter Article
            </Button>
          </Box>

          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.default' }}>
                  <TableCell sx={{ width: '40%' }}>Désignation</TableCell>
                  <TableCell sx={{ width: '15%' }} align="right">Quantité</TableCell>
                  <TableCell sx={{ width: '35%' }}>Commentaire</TableCell>
                  <TableCell sx={{ width: '10%' }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Autocomplete
                        freeSolo
                        options={articles}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') {
                            return option;
                          }
                          return `${option.reference} - ${option.designation}`;
                        }}
                        value={
                          item.article_id
                            ? articles.find((a) => a.id === item.article_id) || null
                            : item.designation || ''
                        }
                        onChange={(_, value) => {
                          if (typeof value === 'string') {
                            handleItemChange(index, 'designation', value);
                            handleItemChange(index, 'article_id', null);
                          } else if (value) {
                            handleArticleChange(index, value);
                          } else {
                            handleItemChange(index, 'designation', '');
                            handleItemChange(index, 'article_id', null);
                          }
                        }}
                        onBlur={(e) => {
                          const inputValue = (e.target as HTMLInputElement).value;
                          if (inputValue && !item.article_id) {
                            handleItemChange(index, 'designation', inputValue);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            multiline
                            rows={3}
                            placeholder="Sélectionner ou saisir une désignation"
                            sx={{
                              '& .MuiInputBase-root': {
                                alignItems: 'flex-start',
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body2">
                                {option.reference} - {option.designation}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="text"
                        size="small"
                        value={item.quantite ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            handleItemChange(index, 'quantite', null);
                          } else if (/^\d*[.,]?\d*$/.test(value)) {
                            handleItemChange(index, 'quantite', value);
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value && value !== '') {
                            const numValue = parseFloat(value.replace(',', '.'));
                            if (!isNaN(numValue)) {
                              handleItemChange(index, 'quantite', numValue);
                            } else {
                              handleItemChange(index, 'quantite', null);
                            }
                          }
                        }}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={item.commentaire}
                        onChange={(e) => handleItemChange(index, 'commentaire', e.target.value)}
                        fullWidth
                        placeholder="Commentaire optionnel"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length === 1}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />

          {/* Transport Information */}
          <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
            Informations Transport
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Nom du Transporteur"
                fullWidth
                size="small"
                value={formData.transporteur_nom}
                onChange={(e) => setFormData({ ...formData, transporteur_nom: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Matricule Véhicule"
                fullWidth
                size="small"
                value={formData.transporteur_matricule}
                onChange={(e) => setFormData({ ...formData, transporteur_matricule: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="CIN Transporteur"
                fullWidth
                size="small"
                value={formData.transporteur_cin}
                onChange={(e) => setFormData({ ...formData, transporteur_cin: e.target.value })}
              />
            </Grid>
          </Grid>

          {/* Conditions/Remarques */}
          <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
            Remarques
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            value={formData.condition_remarque}
            onChange={(e) => setFormData({ ...formData, condition_remarque: e.target.value })}
            placeholder="Conditions de livraison, remarques..."
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            Annuler
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading || (!isEdit && isPlafondDepasse)}
            sx={{
              backgroundColor: '#4CAF50',
              '&:hover': { backgroundColor: '#388E3C' },
            }}
          >
            {loading ? 'Sauvegarde...' : isEdit ? 'Modifier' : 'Créer le BL'}
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={open && minimized}
        color="#4CAF50"
        title={isEdit ? 'Modifier le Bon de Livraison' : 'Nouveau Bon de Livraison'}
        onRestore={restore}
        onClose={handleClose}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </>
  );
};

export default BonLivraisonFormDialog;
